import type { ReactNode } from 'react';
import type { PartnerCatalogueRow } from '@dru/types';
import { DetailSection, Facts, InfoTip } from '@dru/ui';

interface ServiceFact { label: string; value: ReactNode; hint?: string }

/**
 * The service facts alone — NABL scope, home collection, paused tests,
 * turnaround — for a page that sets them beside its own facts.
 * Equipment and agreements are not recorded for partners, so they are left
 * out rather than printed as two rows of "Not tracked".
 */
export function partnerServiceFacts(capabilities: PartnerCatalogueRow[]): ServiceFact[] {
  const total = capabilities.length;
  const nabl = capabilities.filter((c) => c.nablScope).length;
  const home = capabilities.filter((c) => c.homeCollection).length;
  const paused = capabilities.filter((c) => c.paused);
  const tats = capabilities.map((c) => c.tatMinutes).filter((m): m is number => m != null && m > 0).sort((a, b) => a - b);
  const median = tats.length ? tats[Math.floor(tats.length / 2)]! : null;
  const none = <span className="text-neutral-500">Not recorded</span>;
  const hours = (m: number) => (m < 120 ? `${m} min` : `${Math.round(m / 60)} h`);
  return [
    {
      label: 'NABL accredited tests',
      value: total ? `${nabl} of ${total}` : none,
      hint: total && !nabl ? 'None marked inside NABL scope' : undefined,
    },
    { label: 'Home collection', value: total ? (home ? `${home} test${home === 1 ? '' : 's'}` : 'Not offered') : none },
    {
      label: 'Paused today',
      value: paused.length
        ? <span className="font-medium text-overdue-600">{paused.length} test{paused.length === 1 ? '' : 's'} — not routed here</span>
        : total ? 'None' : none,
      hint: paused.length ? paused.slice(0, 3).map((c) => c.name + (c.pausedReason ? ` (${c.pausedReason})` : '')).join(', ') + (paused.length > 3 ? '…' : '') : undefined,
    },
    {
      label: 'Typical turnaround',
      value: median != null ? hours(median) : none,
      hint: median != null ? `Promised on ${tats.length} of ${total} tests` : undefined,
    },
  ];
}

/**
 * What the partner's declared catalogue says about it as a venue: how much of
 * it sits inside NABL scope, what it collects at home, what is paused today
 * and the turnaround it promises. Read from the capability rows the partner
 * record already carries, so it cannot disagree with Branches & tests.
 *
 * Equipment and signed agreements are not recorded for partners, so they are
 * named as such rather than left out silently.
 */
export function PartnerServicesCard({ capabilities, className }: { capabilities: PartnerCatalogueRow[]; className?: string }) {
  const total = capabilities.length;
  const nabl = capabilities.filter((c) => c.nablScope).length;
  const home = capabilities.filter((c) => c.homeCollection).length;
  const paused = capabilities.filter((c) => c.paused);
  const tats = capabilities.map((c) => c.tatMinutes).filter((m): m is number => m != null && m > 0).sort((a, b) => a - b);
  const median = tats.length ? tats[Math.floor(tats.length / 2)]! : null;
  const none = <span className="text-neutral-500">Not recorded</span>;
  const hours = (m: number) => (m < 120 ? `${m} min` : `${Math.round(m / 60)} h`);

  return (
    <DetailSection
      title="Accreditation & services"
      className={className}
      action={(
        <InfoTip label="About accreditation and services">
          From the tests this partner has declared. Change them under Branches &amp; tests.
        </InfoTip>
      )}
    >
      <Facts
        columns={2}
        facts={[
          {
            label: 'NABL accredited tests',
            value: total ? `${nabl} of ${total}` : none,
            hint: total && !nabl ? 'No declared test is marked inside NABL scope' : undefined,
          },
          { label: 'Home collection', value: total ? (home ? `${home} test${home === 1 ? '' : 's'}` : 'Not offered') : none },
          {
            label: 'Paused today',
            value: paused.length
              ? <span className="font-medium text-warning-700">{paused.length} test{paused.length === 1 ? '' : 's'} — not routed here</span>
              : total ? 'None' : none,
            hint: paused.length ? paused.slice(0, 3).map((c) => c.name + (c.pausedReason ? ` (${c.pausedReason})` : '')).join(', ') + (paused.length > 3 ? '…' : '') : undefined,
          },
          {
            label: 'Typical turnaround',
            value: median != null ? hours(median) : none,
            hint: median != null ? `Promised on ${tats.length} of ${total} tests` : undefined,
          },
          { label: 'Equipment', value: <span className="text-neutral-500">Not tracked for partners</span> },
          { label: 'Agreements', value: <span className="text-neutral-500">Not tracked — rate cards are under Rates &amp; statements</span> },
        ]}
      />
    </DetailSection>
  );
}
